import { Box, Grid, Tabs, Tab } from "@mui/material";
import Header from "../../../components/Header";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import OtDataGrid from "../../../components/dataGridOt";
import OtTimeSeriesChart from "../../../components/OtTimeSeriesChart";
import OtActivityStats from "../../../components/OtActivityStats";
import OtDeviceSignalSelector from "../../../components/OtDeviceSignalSelector";

dayjs.extend(utc);
dayjs.extend(timezone);

const OtMonitor = () => {
  const [activeTab, setActiveTab] = useState("stats");
  const [checkReady, setCheckReady] = useState(false);
  const [timeFilter, setTimeFilter] = useState(null);
  const [filterModel, setFilterModel] = useState([]);
  const [signalRows, setSignalRows] = useState([]);
  const [signalStats, setSignalStats] = useState(null);
  const [signalConfigRange, setSignalConfigRange] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [selectedSignalLabel, setSelectedSignalLabel] = useState(null);
  const [panelRow, setPanelRow] = useState(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tz = window.sessionStorage.getItem("timezone") || "Etc/GMT";
  const [payload, setPayload] = useState({
    dateFrom: "",
    dateTo: "",
    isPrivateSource: "",
    isPrivateDestination: "",
  });

  useEffect(() => {
    let dateFrom;
    let dateTo;
    const ipSource = queryParams.get("ipSource");
    const ipDestination = queryParams.get("ipDestination");
    const isPrivateSource = queryParams.get("isPrivateSource");
    const isPrivateDestination = queryParams.get("isPrivateDestination");

    if (queryParams.get("clickedData")) {
      dateFrom = dayjs.tz(queryParams.get("clickedData"), tz).startOf("hour");
      dateTo = dateFrom.endOf("hour").millisecond(0);
    } else if (queryParams.get("advtimeStamp")) {
      dateFrom = dayjs.tz(queryParams.get("advtimeStamp"), tz).startOf("day");
      dateTo = dayjs.tz(queryParams.get("advtimeStamp"), tz).endOf("day").millisecond(0);
    } else if (queryParams.get("dateFrom") && queryParams.get("dateTo")) {
      dateFrom = dayjs.tz(queryParams.get("dateFrom"), tz);
      dateTo = dayjs.tz(queryParams.get("dateTo"), tz).endOf("day").millisecond(0);
    } else {
      dateFrom = dayjs().tz(tz).subtract(1, "hour");
      dateTo = dayjs().tz(tz);
    }

    setPayload((prev) => ({
      ...prev,
      dateFrom,
      dateTo,
      ipsource: ipSource || "",
      ipDestination: ipDestination || "",
      isPrivateSource,
      isPrivateDestination,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- queryParams is a new URLSearchParams object every render; adding it (and tz) would refire this mount-only effect and re-trigger setPayload on every render
  }, []);

  useEffect(() => {
    const hasRequiredFields =
      payload.dateFrom && payload.dateFrom !== "" && payload.dateTo && payload.dateTo !== "";
    setCheckReady(hasRequiredFields);
    if (hasRequiredFields) {
      setTimeFilter([payload.dateFrom.toISOString(), payload.dateTo.toISOString()]);
    }
  }, [payload]);

  // Fired by the global time filter (rendered once, above both tabs) — updates the same
  // `payload` the grid's other filters live on.
  const handleDateChange = ({ dateFrom, dateTo }) => {
    setPayload((prev) => ({ ...prev, dateFrom, dateTo }));
  };

  return (
    <Box m="20px">
      <Grid container>
        <Grid size={12} sx={{ display: "flex", justifyContent: "space-between" }}>
          <Header
            sx={{ width: { xs: "40vw", md: "" } }}
            title="OT Monitor"
            subtitle=""
            desc="Reads & Writes — select a signal to compare its value trend against the historical statistical reference (IQR band, median, mean, fences)."
          />
        </Grid>

        {/* Device/signal/time selection — global to both tabs, so switching to Statistics and
            back doesn't lose or hide the current selection. */}
        <Grid size={12}>
          <OtDeviceSignalSelector
            selectedDevice={selectedDevice}
            onDeviceChange={setSelectedDevice}
            selectedSignal={selectedSignal}
            onSignalChange={(fullAddress, meta) => {
              setSelectedSignal(fullAddress);
              setSelectedSignalLabel(meta?.name ?? null);
            }}
            dateFrom={payload.dateFrom}
            dateTo={payload.dateTo}
            onDateChange={handleDateChange}
          />
        </Grid>

        <Grid size={12}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={tabStyle.tabList}
          >
            <Tab value="stats" label="Statistics" sx={tabStyle.tab} />
            <Tab value="chart" label="Chart & Table" sx={tabStyle.tab} />
          </Tabs>
        </Grid>

        {/* Both tabs stay mounted (toggled via display, not unmounted) so switching between
            them never resets the chart's internal fetch state. */}
        <Grid size={12} sx={{ display: activeTab === "stats" ? "block" : "none", mt: 2.5 }}>
          <OtActivityStats
            ip={selectedDevice}
            fullAddress={selectedSignal}
            signalLabel={selectedSignalLabel}
            timeFilter={timeFilter}
          />
        </Grid>

        <Grid size={12} sx={{ display: activeTab === "chart" ? "block" : "none" }}>
          <Grid container>
            {/* Chart — fetches its own data for the globally-selected device/signal, and
                passes back filtered rows for the grid below */}
            <Grid size={12} sx={{ mt: 2.5 }}>
              <OtTimeSeriesChart
                selectedDevice={selectedDevice}
                selectedSignal={selectedSignal}
                signalLabel={selectedSignalLabel}
                timeFilter={timeFilter}
                filterModel={filterModel}
                onDataChange={(rows, stats, configRange) => {
                  setSignalRows(selectedSignal ? rows : []);
                  setSignalStats(selectedSignal ? stats : null);
                  setSignalConfigRange(selectedSignal ? configRange : null);
                }}
                onPointClick={setPanelRow}
              />
            </Grid>

            {/* Grid — shows all records; when a signal is selected, localRows shows only
                the signal-specific rows returned by the chart endpoint */}
            <Grid size={12}>
              {checkReady ? (
                <OtDataGrid
                  tipo={3}
                  passedPayload={payload}
                  localRows={signalRows}
                  onFilterModelChange={setFilterModel}
                  anomalyMode="outOfRange"
                  panelRow={panelRow}
                  onPanelRowChange={setPanelRow}
                  stats={signalStats}
                  configRange={signalConfigRange}
                  hideTimeFilter
                />
              ) : null}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OtMonitor;

const tabStyle = {
  tab: {
    color: "#eaeaea",
    fontWeight: 600,
    fontFamily: "Poppins",
  },
  tabList: {
    backgroundColor: "#1E1E1E",
    borderRadius: 1,
    "& .MuiTabs-indicator": {
      backgroundColor: "#459296",
    },
  },
};
